import React, { useState, useEffect } from "react";
import firebaseAnalyticsService from "../../services/firebaseAnalyticsService";
import MetricCard from "./components/MetricCard";
import EngagementCard from "./components/EngagementCard";
import ChartCard from "./components/ChartCard";
import StatCard from "./components/StatCard";
import DiagnosticPanel, { DiagnosticModal } from "./components/DiagnosticPanel";

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showDiagnostics, setShowDiagnostics] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🚀 Loading dashboard data...");

      // Test Firebase connection first
      const connectionOk = await firebaseAnalyticsService.testConnection();
      if (!connectionOk) {
        throw new Error(
          "Failed to connect to Firebase. Please check your configuration."
        );
      }

      // Load dashboard data
      const dashboardData = await firebaseAnalyticsService.getDashboardData();
      setData(dashboardData);
      setLastRefresh(new Date());

      console.log("✅ Dashboard data loaded successfully");
    } catch (err) {
      console.error("❌ Error loading dashboard data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = (dailyAnalytics) => {
    return dailyAnalytics
      .slice(0, 14) // Last 14 days
      .reverse()
      .map((day) => ({
        name: firebaseAnalyticsService.formatDate(day.id).slice(0, 5),
        users: Array.isArray(day.unique_users) ? day.unique_users.length : 0,
        sessions: day.total_sessions || 0,
        bookings: day.bookings_completed || 0,
      }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            Loading analytics data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadDashboardData}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.periodMetrics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    );
  }

  const {
    periodMetrics,
    dailyAnalytics,
    bookingEvents,
    signupEvents,
    clubViews,
    sliderTaps,
    availabilitySearch,
    screenAnalytics,
    summary,
  } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Diagnostic Panel */}
      <DiagnosticPanel
        debugInfo={data?._debug}
        onToggle={() => setShowDiagnostics(!showDiagnostics)}
      />
      <DiagnosticModal
        debugInfo={data?._debug}
        isOpen={showDiagnostics}
        onClose={() => setShowDiagnostics(false)}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                📊 Sports App Analytics
              </h1>
              <p className="text-blue-100">
                Real-time insights and performance metrics
              </p>
            </div>
            <div className="text-right">
              <button
                onClick={loadDashboardData}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-md flex items-center gap-2"
              >
                <span>🔄</span>
                Refresh Data
              </button>
              <p className="text-xs text-blue-200 mt-2">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {[
              { id: "overview", name: "📈 Overview" },
              { id: "daily", name: "📅 Daily Metrics" },
              { id: "screens", name: "📱 Screen Analytics" },
              { id: "bookings", name: "🎯 Bookings" },
              { id: "signups", name: "👋 Signups" },
              { id: "clubs", name: "🏢 Clubs" },
              { id: "engagement", name: "💫 Engagement" },
              { id: "trends", name: "📊 Trends" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Unique Users"
                icon="👥"
                color="blue"
                periods={[
                  { label: "30 days", value: periodMetrics["30d"].uniqueUsers },
                  { label: "7 days", value: periodMetrics["7d"].uniqueUsers },
                  { label: "1 day", value: periodMetrics["1d"].uniqueUsers },
                ]}
              />

              <MetricCard
                title="User Sessions"
                icon="🔄"
                color="green"
                periods={[
                  { label: "30 days", value: periodMetrics["30d"].sessions },
                  { label: "7 days", value: periodMetrics["7d"].sessions },
                  { label: "1 day", value: periodMetrics["1d"].sessions },
                ]}
              />

              <MetricCard
                title="Bookings Completed"
                icon="✅"
                color="purple"
                periods={[
                  {
                    label: "30 days",
                    value: periodMetrics["30d"].bookingsCompleted,
                  },
                  {
                    label: "7 days",
                    value: periodMetrics["7d"].bookingsCompleted,
                  },
                  {
                    label: "1 day",
                    value: periodMetrics["1d"].bookingsCompleted,
                  },
                ]}
              />

              <MetricCard
                title="Bookings Started"
                icon="🎯"
                color="orange"
                periods={[
                  {
                    label: "30 days",
                    value: periodMetrics["30d"].bookingsStarted,
                  },
                  {
                    label: "7 days",
                    value: periodMetrics["7d"].bookingsStarted,
                  },
                  {
                    label: "1 day",
                    value: periodMetrics["1d"].bookingsStarted,
                  },
                ]}
              />
            </div>

            {/* Engagement Time Card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <EngagementCard
                value={periodMetrics.averageEngagementTime}
                label="Average Engagement Time"
                subtitle="Per session (Monthly)"
              />

              <div className="lg:col-span-2">
                <StatCard
                  title="Booking Conversion Rate (30 days)"
                  value={`${periodMetrics["30d"].conversionRate}%`}
                  subtitle={`${periodMetrics["30d"].bookingsCompleted} completed / ${periodMetrics["30d"].bookingsStarted} started`}
                  color="emerald"
                  trend={
                    parseFloat(periodMetrics["30d"].conversionRate) >= 80
                      ? "up"
                      : parseFloat(periodMetrics["30d"].conversionRate) >= 60
                      ? "neutral"
                      : "down"
                  }
                  change={`${periodMetrics["30d"].conversionRate}%`}
                />
              </div>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Bookings (30d)"
                value={periodMetrics["30d"].bookingsCompleted}
                subtitle="Completed bookings"
                color="indigo"
              />
              <StatCard
                title="Recent Signups (7d)"
                value={
                  signupEvents.filter((e) => {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                    return e.timestamp >= sevenDaysAgo;
                  }).length
                }
                subtitle="New users"
                color="pink"
              />
              <StatCard
                title="Active Sessions (1d)"
                value={periodMetrics["1d"].sessions}
                subtitle="Today's sessions"
                color="yellow"
              />
              <StatCard
                title="Conversion (7d)"
                value={`${periodMetrics["7d"].conversionRate}%`}
                subtitle="Weekly conversion"
                color="emerald"
              />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>🎯</span> Recent Bookings
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {bookingEvents.slice(0, 10).map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {event.event_type === "booking_completed"
                            ? "✅"
                            : "🎯"}{" "}
                          {event.event_type}
                        </p>
                        <p className="text-sm text-gray-600">
                          {event.booking_data?.club_name || "Unknown Club"}
                        </p>
                      </div>
                      <div className="text-right">
                        {event.booking_data?.total_amount && (
                          <p className="text-sm font-medium text-green-600">
                            ${event.booking_data.total_amount}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {firebaseAnalyticsService.formatTimestamp(
                            event.timestamp
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>👋</span> Recent Signups
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {signupEvents.slice(0, 10).map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">New User</p>
                        <p className="text-sm text-gray-600">
                          Method:{" "}
                          {event.signup_data?.signup_method || "Unknown"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {firebaseAnalyticsService.formatTimestamp(
                            event.timestamp
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "trends" && (
          <div className="space-y-6">
            <ChartCard
              title="User Activity Over Time (Last 14 Days)"
              data={prepareChartData(dailyAnalytics)}
              type="area"
              dataKeys={["users", "sessions"]}
              colors={["#3B82F6", "#10B981"]}
            />

            <ChartCard
              title="Booking Trends (Last 14 Days)"
              data={prepareChartData(dailyAnalytics)}
              type="bar"
              dataKeys={["bookings"]}
              colors={["#8B5CF6"]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard
                title="Daily Users vs Sessions"
                data={prepareChartData(dailyAnalytics)}
                type="line"
                dataKeys={["users", "sessions"]}
                colors={["#3B82F6", "#10B981"]}
              />

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Period Comparison
                </h3>
                <div className="space-y-4">
                  {["1d", "7d", "30d"].map((period) => (
                    <div
                      key={period}
                      className="border-l-4 border-blue-500 pl-4"
                    >
                      <p className="text-sm text-gray-600 font-medium mb-2">
                        {period === "1d"
                          ? "Today"
                          : period === "7d"
                          ? "Last 7 Days"
                          : "Last 30 Days"}
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Users</p>
                          <p className="text-xl font-bold text-blue-600">
                            {periodMetrics[period].uniqueUsers}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Bookings</p>
                          <p className="text-xl font-bold text-purple-600">
                            {periodMetrics[period].bookingsCompleted}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "bookings" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard
                title="Total Bookings (30d)"
                value={periodMetrics["30d"].bookingsCompleted}
                subtitle="Completed"
                color="purple"
              />
              <StatCard
                title="Conversion Rate (30d)"
                value={`${periodMetrics["30d"].conversionRate}%`}
                subtitle="Success rate"
                color="emerald"
              />
              <StatCard
                title="Started but Not Completed"
                value={
                  periodMetrics["30d"].bookingsStarted -
                  periodMetrics["30d"].bookingsCompleted
                }
                subtitle="Drop-offs"
                color="orange"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                All Booking Events ({bookingEvents.length} total)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Event Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Club
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Sport
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Timestamp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookingEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              event.event_type === "booking_completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {event.event_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {event.booking_data?.club_name || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {event.booking_data?.sport_name || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {event.booking_data?.total_amount
                            ? `$${event.booking_data.total_amount}`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {firebaseAnalyticsService.formatTimestamp(
                            event.timestamp
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "daily" && (
          <div className="space-y-6">
            {/* Daily Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Today's Unique Users"
                value={
                  dailyAnalytics[0]?.unique_users
                    ? Array.isArray(dailyAnalytics[0].unique_users)
                      ? dailyAnalytics[0].unique_users.length
                      : 0
                    : 0
                }
                color="blue"
              />
              <StatCard
                title="Today's Sessions"
                value={dailyAnalytics[0]?.total_sessions || 0}
                color="green"
              />
              <StatCard
                title="Today's Signups"
                value={dailyAnalytics[0]?.signups || 0}
                color="purple"
              />
              <StatCard
                title="Today's Bookings"
                value={dailyAnalytics[0]?.bookings_completed || 0}
                subtitle={`${dailyAnalytics[0]?.bookings_started || 0} started`}
                color="orange"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Daily Analytics ({dailyAnalytics.length} days)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unique Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sessions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Signups
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bookings Started
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bookings Completed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conversion Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dailyAnalytics.map((day) => {
                      const users = day.unique_users || [];
                      const uniqueUsers = Array.isArray(users)
                        ? users.length
                        : 0;
                      const rate =
                        firebaseAnalyticsService.calculateConversionRate(
                          day.bookings_started || 0,
                          day.bookings_completed || 0
                        );

                      return (
                        <tr key={day.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {firebaseAnalyticsService.formatDate(day.id)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {uniqueUsers}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {day.total_sessions || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {day.signups || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {day.bookings_started || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {day.bookings_completed || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                parseFloat(rate) >= 80
                                  ? "bg-green-100 text-green-800"
                                  : parseFloat(rate) >= 60
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {rate}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {firebaseAnalyticsService.formatTimestamp(
                              day.last_updated
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "screens" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <StatCard
                title="Total Screens"
                value={summary.totalScreens}
                color="blue"
              />
              <StatCard
                title="Total Time"
                value={`${summary.totalTimeMinutes}m`}
                color="green"
              />
              <StatCard
                title="Total Sessions"
                value={summary.totalSessions}
                color="purple"
              />
              <StatCard
                title="Avg per Screen"
                value={`${summary.averageTimePerScreen}m`}
                color="orange"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Screen Analytics ({screenAnalytics.length} screens)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Screen
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sessions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avg Session
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Engagement
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {screenAnalytics.map((screen) => (
                      <tr key={screen.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {screen.screenName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {screen.totalTimeMinutes} min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {screen.totalSessions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {screen.averageSessionMinutes} min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="px-2 py-1 text-xs font-medium rounded-full text-white"
                            style={{
                              backgroundColor:
                                firebaseAnalyticsService.getEngagementColor(
                                  screen.engagementLevel
                                ),
                            }}
                          >
                            {screen.engagementLevel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "signups" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard
                title="Total Signups (30d)"
                value={signupEvents.length}
                color="purple"
              />
              <StatCard
                title="Weekly Signups"
                value={
                  signupEvents.filter((e) => {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                    return e.timestamp >= sevenDaysAgo;
                  }).length
                }
                color="blue"
              />
              <StatCard
                title="Today's Signups"
                value={
                  signupEvents.filter((e) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return e.timestamp >= today;
                  }).length
                }
                color="green"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Signup Events ({signupEvents.length} total)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Signup Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Session ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {signupEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {event.user_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                            {event.signup_data?.signup_method || "Unknown"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {event.signup_data?.city || "Unknown"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {event.session_id
                            ? event.session_id.substring(0, 8) + "..."
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {firebaseAnalyticsService.formatTimestamp(
                            event.timestamp
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "clubs" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Most Viewed Clubs ({clubViews.length} clubs)
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {clubViews.map((club, index) => (
                    <div
                      key={club.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {club.club_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            ID: {club.club_id}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">
                          {club.view_count}
                        </p>
                        <p className="text-sm text-gray-500">views</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Availability Search Analytics
                </h3>
                {availabilitySearch ? (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-600 font-medium">
                          Total Searches:
                        </span>
                        <span className="text-3xl font-bold text-blue-600">
                          {availabilitySearch.total_searches || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">
                          Last Search:
                        </span>
                        <span className="text-sm text-gray-500">
                          {firebaseAnalyticsService.formatTimestamp(
                            availabilitySearch.last_updated
                          )}
                        </span>
                      </div>
                    </div>
                    {availabilitySearch.last_search_data && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-semibold text-gray-900 mb-3">
                          Last Search Details:
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500">Sport</p>
                            <p className="text-sm font-medium text-gray-900">
                              {availabilitySearch.last_search_data.sport_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">City</p>
                            <p className="text-sm font-medium text-gray-900">
                              {availabilitySearch.last_search_data.city_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Duration</p>
                            <p className="text-sm font-medium text-gray-900">
                              {
                                availabilitySearch.last_search_data
                                  .duration_minutes
                              }{" "}
                              min
                            </p>
                          </div>
                          {availabilitySearch.last_search_data.date && (
                            <div>
                              <p className="text-xs text-gray-500">Date</p>
                              <p className="text-sm font-medium text-gray-900">
                                {availabilitySearch.last_search_data.date}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-4xl mb-2">🔍</p>
                    <p>No availability search data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Club Views Chart */}
            {clubViews.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Top 10 Clubs by Views
                </h3>
                <ChartCard
                  title=""
                  data={clubViews.slice(0, 10).map((club) => ({
                    name: club.club_name.substring(0, 20),
                    views: club.view_count,
                  }))}
                  type="bar"
                  dataKeys={["views"]}
                  colors={["#3B82F6"]}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === "engagement" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Slider Engagement ({sliderTaps.length} sliders)
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sliderTaps.map((slider, index) => (
                    <div
                      key={slider.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="bg-purple-100 text-purple-600 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                          <p className="font-medium text-gray-900">
                            {slider.slider_id}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600">
                          Type: {slider.slider_type || "Unknown"}
                        </p>
                        {slider.last_tap_data?.url && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            URL: {slider.last_tap_data.url.substring(0, 40)}...
                          </p>
                        )}
                        {slider.last_tap_data?.title && (
                          <p className="text-xs text-gray-600 mt-1">
                            Title: {slider.last_tap_data.title}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-2xl font-bold text-purple-600">
                          {slider.total_taps}
                        </p>
                        <p className="text-sm text-gray-500">taps</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {firebaseAnalyticsService.formatTimestamp(
                            slider.last_updated
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Summary Statistics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <span className="text-gray-600 font-medium">
                      Total Screens:
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      {summary.totalScreens}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <span className="text-gray-600 font-medium">
                      Total Time:
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {summary.totalTimeMinutes} min
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                    <span className="text-gray-600 font-medium">
                      Total Sessions:
                    </span>
                    <span className="text-2xl font-bold text-purple-600">
                      {summary.totalSessions}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
                    <span className="text-gray-600 font-medium">
                      Avg Time per Screen:
                    </span>
                    <span className="text-2xl font-bold text-orange-600">
                      {summary.averageTimePerScreen} min
                    </span>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <span className="text-gray-600 font-medium block mb-2">
                      Most Popular Screen:
                    </span>
                    <span className="text-xl font-bold text-indigo-600">
                      {summary.mostPopularScreen}
                    </span>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-lg">
                    <span className="text-gray-600 font-medium block mb-2">
                      Total Slider Taps:
                    </span>
                    <span className="text-xl font-bold text-pink-600">
                      {sliderTaps.reduce(
                        (sum, slider) => sum + (slider.total_taps || 0),
                        0
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Slider Taps Chart */}
            {sliderTaps.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <ChartCard
                  title="Top Sliders by Engagement"
                  data={sliderTaps.slice(0, 10).map((slider) => ({
                    name: slider.slider_id.substring(0, 15),
                    taps: slider.total_taps,
                  }))}
                  type="bar"
                  dataKeys={["taps"]}
                  colors={["#8B5CF6"]}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard
                title="Total Users (30d)"
                value={periodMetrics["30d"].uniqueUsers}
                subtitle="Unique users"
                color="blue"
              />
              <StatCard
                title="Weekly Active Users"
                value={periodMetrics["7d"].uniqueUsers}
                subtitle="Last 7 days"
                color="green"
              />
              <StatCard
                title="Daily Active Users"
                value={periodMetrics["1d"].uniqueUsers}
                subtitle="Today"
                color="purple"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Recent Signups ({signupEvents.length} total)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Signup Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        City
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Timestamp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {signupEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {event.user_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                            {event.signup_data?.signup_method || "Unknown"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {event.signup_data?.city || "Unknown"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {firebaseAnalyticsService.formatTimestamp(
                            event.timestamp
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
